import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  FileText,
  Receipt,
  QrCode,
  Copy,
  Check,
  ExternalLink,
  AlertCircle,
  CheckCircle2,
  Clock,
  XCircle,
  Building2,
  Loader2,
} from 'lucide-react';
import {
  faturamentoService,
  type CustomerFaturamentoItemContract,
  type StatusFaturamentoCliente,
} from '../services/faturamento.service';
import { useEmpresa } from '../contexts/EmpresaContext';
import PageContainer from '../components/PageContainer';
import PageHeader from '../components/PageHeader';
import Modal from '../components/Modal';

export default function Faturamento() {
  const { empresa } = useEmpresa();
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [search, setSearch] = useState<string>('');
  const [modalBoletoFatura, setModalBoletoFatura] = useState<CustomerFaturamentoItemContract | null>(null);
  const [copiadoTipo, setCopiadoTipo] = useState<'linha' | 'pix' | null>(null);
  const [loadingDoc, setLoadingDoc] = useState<{ id: number; tipo: string } | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const {
    data: faturamentoResponse,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['customer-faturamento', empresa?.id, statusFilter],
    queryFn: () => faturamentoService.findAll({ status: statusFilter !== 'all' ? statusFilter : undefined }),
    enabled: !!empresa?.id,
  });

  const faturas = useMemo(() => {
    if (!faturamentoResponse?.data) return [];
    let list = faturamentoResponse.data;
    if (search.trim()) {
      const q = search.toLowerCase().trim();
      list = list.filter(
        (f) =>
          f.numero_fatura?.toLowerCase().includes(q) ||
          f.competencia?.toLowerCase().includes(q) ||
          f.numero_nfse?.toLowerCase().includes(q),
      );
    }
    return list;
  }, [faturamentoResponse, search]);

  // KPIs
  const kpis = useMemo(() => {
    const raw = faturamentoResponse?.data || [];
    const total = raw.length;
    const pagas = raw.filter((f) => f.status === 'PAGO');
    const aVencer = raw.filter((f) => f.status === 'A_VENCER');
    const vencidas = raw.filter((f) => f.status === 'VENCIDO');

    const totalValorPagas = pagas.reduce((acc, f) => acc + (f.valor_total || 0), 0);
    const totalValorAVencer = aVencer.reduce((acc, f) => acc + (f.valor_total || 0), 0);
    const totalValorVencidas = vencidas.reduce((acc, f) => acc + (f.valor_total || 0), 0);

    return {
      total,
      qtdPagas: pagas.length,
      qtdAVencer: aVencer.length,
      qtdVencidas: vencidas.length,
      totalValorPagas,
      totalValorAVencer,
      totalValorVencidas,
    };
  }, [faturamentoResponse]);

  const handleAbrirDoc = async (fatura: CustomerFaturamentoItemContract, tipo: 'espelho' | 'boleto' | 'nfse') => {
    try {
      setActionError(null);
      setLoadingDoc({ id: fatura.id, tipo });
      await faturamentoService.abrirDocumento(fatura.id, tipo);
    } catch (err: unknown) {
      console.error(`Erro ao abrir ${tipo}:`, err);
      const msg = err instanceof Error ? err.message : String(err);
      setActionError(`Não foi possível abrir o documento (${msg || 'erro interno'}). Tente novamente.`);
    } finally {
      setLoadingDoc(null);
    }
  };

  const handleCopiar = (texto: string, tipo: 'linha' | 'pix') => {
    navigator.clipboard.writeText(texto);
    setCopiadoTipo(tipo);
    setTimeout(() => setCopiadoTipo(null), 2500);
  };

  const formatCurrency = (val?: number | null) => {
    if (val === undefined || val === null) return 'R$ 0,00';
    return val.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  const formatDate = (iso?: string | null) => {
    if (!iso) return '-';
    try {
      const d = new Date(iso);
      return d.toLocaleDateString('pt-BR', { timeZone: 'UTC' });
    } catch {
      return iso;
    }
  };

  const renderBadgeStatus = (status: StatusFaturamentoCliente, fatura: CustomerFaturamentoItemContract) => {
    switch (status) {
      case 'PAGO':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
            Pago
            {fatura.data_pagamento && (
              <span className="text-[10px] text-emerald-600 font-normal">
                ({formatDate(fatura.data_pagamento)})
              </span>
            )}
          </span>
        );
      case 'A_VENCER':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200">
            <Clock className="w-3.5 h-3.5 text-blue-600" />
            A Vencer
          </span>
        );
      case 'VENCIDO':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-rose-50 text-rose-700 border border-rose-200">
            <AlertCircle className="w-3.5 h-3.5 text-rose-600" />
            Vencido
            {fatura.dias_atraso !== null && fatura.dias_atraso !== undefined && fatura.dias_atraso > 0 && (
              <span className="text-[10px] text-rose-600 font-normal">
                ({fatura.dias_atraso}d em atraso)
              </span>
            )}
          </span>
        );
      case 'CANCELADO':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-600 border border-slate-200">
            <XCircle className="w-3.5 h-3.5 text-slate-500" />
            Cancelado
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">
            <Clock className="w-3.5 h-3.5 text-amber-600" />
            Em Análise
          </span>
        );
    }
  };

  return (
    <PageContainer>
      <div className="space-y-6">
        <PageHeader
          title="Faturas e Boletos Corporativos"
          description={`Acompanhe o histórico de faturamento mensal, documentos fiscais e conciliação bancária da ${empresa?.nome || 'sua empresa'}.`}
        />

        {actionError && (
          <div className="bg-rose-50 border border-rose-200 text-rose-700 px-4 py-3 rounded-lg flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-rose-600 flex-shrink-0" />
              <span>{actionError}</span>
            </div>
            <button
              onClick={() => setActionError(null)}
              className="text-xs font-bold uppercase tracking-wider text-rose-700 hover:text-rose-900"
            >
              Fechar
            </button>
          </div>
        )}

        {/* CARDS KPIS EXECUTIVOS */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
            <div className="text-xs font-medium text-slate-500 uppercase tracking-wider">Total de Faturas</div>
            <div className="mt-2 flex items-baseline justify-between">
              <span className="text-2xl font-bold text-slate-800">{kpis.total}</span>
              <span className="text-xs text-slate-400">Histórico completo</span>
            </div>
          </div>

          <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm border-l-4 border-l-blue-500">
            <div className="text-xs font-medium text-slate-500 uppercase tracking-wider">A Vencer</div>
            <div className="mt-2 flex items-baseline justify-between">
              <span className="text-2xl font-bold text-blue-700">{kpis.qtdAVencer}</span>
              <span className="text-xs font-semibold text-blue-600">{formatCurrency(kpis.totalValorAVencer)}</span>
            </div>
          </div>

          <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm border-l-4 border-l-emerald-500">
            <div className="text-xs font-medium text-slate-500 uppercase tracking-wider">Pagas (Conciliadas)</div>
            <div className="mt-2 flex items-baseline justify-between">
              <span className="text-2xl font-bold text-emerald-700">{kpis.qtdPagas}</span>
              <span className="text-xs font-semibold text-emerald-600">{formatCurrency(kpis.totalValorPagas)}</span>
            </div>
          </div>

          <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm border-l-4 border-l-rose-500">
            <div className="text-xs font-medium text-slate-500 uppercase tracking-wider">Vencidas</div>
            <div className="mt-2 flex items-baseline justify-between">
              <span className="text-2xl font-bold text-rose-700">{kpis.qtdVencidas}</span>
              <span className="text-xs font-semibold text-rose-600">{formatCurrency(kpis.totalValorVencidas)}</span>
            </div>
          </div>
        </div>

        {/* BARRA DE FILTROS E BUSCA */}
        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="w-full md:w-80">
            <input
              type="text"
              placeholder="Buscar por competência ou fatura..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="flex items-center gap-2 w-full md:w-auto overflow-x-auto">
            <button
              onClick={() => setStatusFilter('all')}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                statusFilter === 'all'
                  ? 'bg-slate-900 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              Todas ({kpis.total})
            </button>
            <button
              onClick={() => setStatusFilter('A_VENCER')}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                statusFilter === 'A_VENCER'
                  ? 'bg-blue-600 text-white'
                  : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
              }`}
            >
              A Vencer ({kpis.qtdAVencer})
            </button>
            <button
              onClick={() => setStatusFilter('PAGO')}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                statusFilter === 'PAGO'
                  ? 'bg-emerald-600 text-white'
                  : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
              }`}
            >
              Pagas ({kpis.qtdPagas})
            </button>
            <button
              onClick={() => setStatusFilter('VENCIDO')}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                statusFilter === 'VENCIDO'
                  ? 'bg-rose-600 text-white'
                  : 'bg-rose-50 text-rose-700 hover:bg-rose-100'
              }`}
            >
              Vencidas ({kpis.qtdVencidas})
            </button>
          </div>
        </div>

        {/* TABELA DE FATURAS */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          {isLoading ? (
            <div className="py-20 flex flex-col items-center justify-center text-slate-400">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600 mb-3" />
              <p className="text-sm">Carregando faturas e conciliação bancária...</p>
            </div>
          ) : isError ? (
            <div className="py-16 text-center text-slate-500">
              <AlertCircle className="w-10 h-10 text-rose-500 mx-auto mb-2" />
              <p className="text-base font-semibold text-slate-700">Erro ao carregar faturas</p>
              <p className="text-xs text-slate-400 mt-1">{error instanceof Error ? error.message : 'Erro de comunicação com o servidor'}</p>
              <button
                onClick={() => refetch()}
                className="mt-4 px-4 py-2 bg-blue-600 text-white text-xs font-semibold rounded-lg hover:bg-blue-700"
              >
                Tentar Novamente
              </button>
            </div>
          ) : faturas.length === 0 ? (
            <div className="py-16 text-center text-slate-400">
              <Building2 className="w-12 h-12 mx-auto mb-3 opacity-40" />
              <p className="text-base font-medium text-slate-600">Nenhuma fatura encontrada</p>
              <p className="text-xs text-slate-400 mt-1">
                {search || statusFilter !== 'all'
                  ? 'Nenhum resultado corresponde aos filtros selecionados.'
                  : 'Sua empresa ainda não possui faturas corporativas emitidas.'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200 text-slate-600 text-xs font-semibold uppercase tracking-wider">
                    <th className="px-6 py-4">Competência / Fatura</th>
                    <th className="px-6 py-4">Vencimento</th>
                    <th className="px-6 py-4">Valor Total</th>
                    <th className="px-6 py-4 text-center">Status Conciliação</th>
                    <th className="px-6 py-4 text-center">Espelho da Fatura</th>
                    <th className="px-6 py-4 text-center">Boleto Bancário</th>
                    <th className="px-6 py-4 text-center">Nota Fiscal (NFS-e)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {faturas.map((fatura) => {
                    const isBoletoLoading =
                      loadingDoc?.id === fatura.id && loadingDoc?.tipo === 'boleto';
                    const isEspelhoLoading =
                      loadingDoc?.id === fatura.id && loadingDoc?.tipo === 'espelho';
                    const isNfseLoading =
                      loadingDoc?.id === fatura.id && loadingDoc?.tipo === 'nfse';

                    return (
                      <tr key={fatura.id} className="hover:bg-slate-50/80 transition-colors">
                        <td className="px-6 py-4">
                          <div className="font-bold text-slate-800 text-base">
                            {fatura.competencia}
                          </div>
                          <div className="text-xs text-slate-500 font-mono mt-0.5">
                            {fatura.numero_fatura}
                          </div>
                        </td>

                        <td className="px-6 py-4 text-slate-600 whitespace-nowrap">
                          <div className="font-semibold text-slate-700">
                            {formatDate(fatura.data_vencimento)}
                          </div>
                          {fatura.data_fechamento && (
                            <div className="text-[11px] text-slate-400">
                              Fechado em: {formatDate(fatura.data_fechamento)}
                            </div>
                          )}
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="font-extrabold text-slate-900 text-base">
                            {formatCurrency(fatura.valor_total)}
                          </div>
                          {fatura.total_itens ? (
                            <div className="text-[11px] text-slate-400">
                              {fatura.total_itens} item(ns) apurado(s)
                            </div>
                          ) : null}
                        </td>

                        <td className="px-6 py-4 text-center whitespace-nowrap">
                          {renderBadgeStatus(fatura.status, fatura)}
                        </td>

                        {/* DOCUMENTO: ESPELHO DA FATURA */}
                        <td className="px-6 py-4 text-center whitespace-nowrap">
                          {fatura.pode_baixar_espelho ? (
                            <button
                              onClick={() => handleAbrirDoc(fatura, 'espelho')}
                              disabled={isEspelhoLoading}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-300 transition-colors shadow-sm disabled:opacity-50"
                              title="Visualizar demonstrativo e espelho detalhado da fatura"
                            >
                              {isEspelhoLoading ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              ) : (
                                <FileText className="w-3.5 h-3.5 text-blue-600" />
                              )}
                              Espelho
                            </button>
                          ) : (
                            <div
                              className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs text-slate-400 bg-slate-50 border border-slate-200 cursor-not-allowed"
                              title="Espelho ainda em processamento"
                            >
                              <FileText className="w-3 h-3 text-slate-300" />
                              Pendente
                            </div>
                          )}
                        </td>

                        {/* DOCUMENTO: BOLETO BANCÁRIO */}
                        <td className="px-6 py-4 text-center whitespace-nowrap">
                          {fatura.pode_baixar_boleto ? (
                            <div className="inline-flex items-center gap-1.5">
                              <button
                                onClick={() => handleAbrirDoc(fatura, 'boleto')}
                                disabled={isBoletoLoading}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 transition-colors shadow-sm disabled:opacity-50"
                                title="Visualizar boleto bancário Itaú oficial com Pix"
                              >
                                {isBoletoLoading ? (
                                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                ) : (
                                  <ExternalLink className="w-3.5 h-3.5 text-blue-600" />
                                )}
                                Boleto
                              </button>
                              {(fatura.linha_digitavel || fatura.pix_copia_e_cola) && (
                                <button
                                  onClick={() => setModalBoletoFatura(fatura)}
                                  className="p-1.5 rounded-lg text-slate-500 hover:text-blue-600 hover:bg-blue-50 border border-slate-200 transition-colors"
                                  title="Copiar Linha Digitável ou Pix Copia-e-Cola"
                                >
                                  <QrCode className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>
                          ) : (
                            <div
                              className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs text-slate-400 bg-slate-50 border border-slate-200 cursor-not-allowed"
                              title="Boleto ainda em registro bancário"
                            >
                              <Clock className="w-3 h-3 text-slate-300" />
                              Pendente
                            </div>
                          )}
                        </td>

                        {/* DOCUMENTO: NOTA FISCAL NFS-e */}
                        <td className="px-6 py-4 text-center whitespace-nowrap">
                          {fatura.pode_baixar_nfse ? (
                            <button
                              onClick={() => handleAbrirDoc(fatura, 'nfse')}
                              disabled={isNfseLoading}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 transition-colors shadow-sm disabled:opacity-50"
                              title={`Visualizar NFS-e ${fatura.numero_nfse ? `nº ${fatura.numero_nfse}` : ''}`}
                            >
                              {isNfseLoading ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              ) : (
                                <Receipt className="w-3.5 h-3.5 text-emerald-600" />
                              )}
                              {fatura.numero_nfse ? `NFS-e ${fatura.numero_nfse}` : 'NFS-e'}
                            </button>
                          ) : (
                            <div
                              className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs text-slate-400 bg-slate-50 border border-slate-200 cursor-not-allowed"
                              title="NFS-e ainda em autorização fiscal"
                            >
                              <Receipt className="w-3 h-3 text-slate-300" />
                              Pendente
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* MODAL DE PAGAMENTO RÁPIDO (LINHA DIGITÁVEL E PIX) */}
      {modalBoletoFatura && (
        <Modal
          isOpen={!!modalBoletoFatura}
          onClose={() => setModalBoletoFatura(null)}
          title={`Pagamento Rápido - Competência ${modalBoletoFatura.competencia}`}
        >
          <div className="space-y-5 pt-2">
            <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
              <div className="flex justify-between items-center text-sm">
                <span className="text-slate-500">Valor da Fatura:</span>
                <span className="font-extrabold text-slate-800 text-base">
                  {formatCurrency(modalBoletoFatura.valor_total)}
                </span>
              </div>
              <div className="flex justify-between items-center text-xs text-slate-500 mt-1">
                <span>Vencimento:</span>
                <span className="font-semibold text-slate-700">
                  {formatDate(modalBoletoFatura.data_vencimento)}
                </span>
              </div>
            </div>

            {/* LINHA DIGITÁVEL */}
            {modalBoletoFatura.linha_digitavel && (
              <div className="space-y-1.5">
                <label className="text-xs font-bold uppercase tracking-wider text-slate-600">
                  Linha Digitável do Boleto
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    readOnly
                    value={modalBoletoFatura.linha_digitavel}
                    className="flex-1 bg-slate-50 border border-slate-300 rounded-lg px-3 py-2 text-xs font-mono text-slate-800 select-all focus:outline-none"
                  />
                  <button
                    onClick={() => handleCopiar(modalBoletoFatura.linha_digitavel!, 'linha')}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold transition-colors"
                  >
                    {copiadoTipo === 'linha' ? (
                      <>
                        <Check className="w-3.5 h-3.5 text-emerald-300" />
                        Copiado!
                      </>
                    ) : (
                      <>
                        <Copy className="w-3.5 h-3.5" />
                        Copiar
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* PIX COPIA E COLA */}
            {modalBoletoFatura.pix_copia_e_cola && (
              <div className="space-y-1.5">
                <label className="text-xs font-bold uppercase tracking-wider text-slate-600 flex items-center gap-1">
                  <QrCode className="w-3.5 h-3.5 text-emerald-600" />
                  Pix Copia e Cola (Liquidação Imediata)
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    readOnly
                    value={modalBoletoFatura.pix_copia_e_cola}
                    className="flex-1 bg-slate-50 border border-slate-300 rounded-lg px-3 py-2 text-xs font-mono text-slate-800 truncate select-all focus:outline-none"
                  />
                  <button
                    onClick={() => handleCopiar(modalBoletoFatura.pix_copia_e_cola!, 'pix')}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold transition-colors"
                  >
                    {copiadoTipo === 'pix' ? (
                      <>
                        <Check className="w-3.5 h-3.5 text-white" />
                        Copiado!
                      </>
                    ) : (
                      <>
                        <Copy className="w-3.5 h-3.5" />
                        Copiar
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            <div className="pt-2 flex justify-end gap-2 border-t border-slate-100">
              <button
                onClick={() => setModalBoletoFatura(null)}
                className="px-4 py-2 rounded-lg text-xs font-semibold text-slate-600 hover:bg-slate-100 transition-colors"
              >
                Fechar
              </button>
              <button
                onClick={() => handleAbrirDoc(modalBoletoFatura, 'boleto')}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold bg-blue-600 hover:bg-blue-700 text-white transition-colors"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                Abrir Boleto Completo
              </button>
            </div>
          </div>
        </Modal>
      )}
    </PageContainer>
  );
}
